import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { apiUrl } from './config';

export default function CustomerViewForm() {
  const navigate = useNavigate();
  const { customerId } = useParams();

  const [formData, setFormData] = useState({
    account_id: '',
    name: '',
    billing_name: '',
    account_rep: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    telephone: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    // Billing address fields
    billing_address_1: '',
    billing_address_2: '',
    billing_city: '',
    billing_state: '',
    billing_zip_code: '',
    // Billing contact fields
    billing_contact_name: '',
    billing_contact_email: '',
    billing_contact_phone: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showBillingAddress, setShowBillingAddress] = useState(false);

  // Fetch customer data on component mount
  useEffect(() => {
    const fetchCustomer = async () => {
      if (customerId) {
        setIsLoading(true);
        
        try {
          console.log('Loading customer for view, ID:', customerId);
          const response = await fetch(apiUrl(`/customer/one?customerId=${customerId}`));
          
          if (response.ok) {
            const customerData = await response.json();
            console.log('Customer data loaded:', customerData);
            
            // Populate form with existing data
            setFormData({
              account_id: customerData.account_id || '',
              name: customerData.name || '',
              billing_name: customerData.billing_name || '',
              account_rep: customerData.account_rep || '',
              address_1: customerData.address_1 || '',
              address_2: customerData.address_2 || '',
              city: customerData.city || '',
              state: customerData.state || '',
              zip_code: customerData.zip_code || '',
              telephone: customerData.telephone || '',
              contact_name: customerData.contact_name || '',
              contact_email: customerData.contact_email || '',
              contact_phone: customerData.contact_phone || '',
              // Billing address fields
              billing_address_1: customerData.billing_address_1 || '',
              billing_address_2: customerData.billing_address_2 || '',
              billing_city: customerData.billing_city || '',
              billing_state: customerData.billing_state || '',
              billing_zip_code: customerData.billing_zip_code || '',
              // Billing contact fields
              billing_contact_name: customerData.billing_contact_name || '',
              billing_contact_email: customerData.billing_contact_email || '',
              billing_contact_phone: customerData.billing_contact_phone || '',
            });
            
            // Show billing address section if any billing fields have data
            const hasBillingData = customerData.billing_address_1 || 
                                 customerData.billing_city || 
                                 customerData.billing_state || 
                                 customerData.billing_zip_code ||
                                 customerData.billing_contact_name ||
                                 customerData.billing_contact_email ||
                                 customerData.billing_contact_phone;
            
            setShowBillingAddress(!!hasBillingData);
            
          } else {
            console.error('Failed to load customer data');
            navigate('/customers');
          }
        } catch (error) {
          console.error('Error loading customer:', error);
          navigate('/customers');
        } finally {
          setIsLoading(false);
        }
      } else {
        navigate('/customers');
      }
    };

    fetchCustomer();
  }, [customerId, navigate]);

  const handleCancel = () => {
    navigate('/customers');
  };

  // Show loading state while fetching customer data
  if (isLoading) {
    return (
      <div className="lg:pl-72 m-3 sm:ml-0 px-0 sm:px-0">
        <div className="text-center py-12">
          <div className="text-gray-500">Loading customer data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:pl-72 m-3 sm:ml-0 px-0 sm:px-0">
      <div className="space-y-12 sm:space-y-16">
        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">View Customer</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
            Customer information for Bandtango and Bandtango related Properties 
          </p>

          <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0">
            
            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Company Name
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xl sm:text-sm sm:leading-6">
                  {formData.name || 'Not provided'}
                </div>
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Billing Name
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xl sm:text-sm sm:leading-6">
                  {formData.billing_name || 'Not provided'}
                </div>
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Account Representative
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                  {formData.account_rep || 'Not provided'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">Company Address Information</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
            Primary business address where your company can receive mail.
          </p>

          <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0">
            
            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Street Address
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xl sm:text-sm sm:leading-6">
                  {formData.address_1 || 'Not provided'}
                </div>
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Address Line 2
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xl sm:text-sm sm:leading-6">
                  {formData.address_2 || 'Not provided'}
                </div>
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                City
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                  {formData.city || 'Not provided'}
                </div>
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                State / Province
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                  {formData.state || 'Not provided'}
                </div>
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                ZIP / Postal Code
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                  {formData.zip_code || 'Not provided'}
                </div>
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Telephone
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                  {formData.telephone || 'Not provided'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Billing Address Section - Only visible if billing data exists */}
        {showBillingAddress && (
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">Billing Address Information</h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
              Address where invoices and billing information should be sent.
            </p>

            <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0">
              
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Billing Street Address
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <div className="block w-full py-1.5 text-gray-900 sm:max-w-xl sm:text-sm sm:leading-6">
                    {formData.billing_address_1 || 'Not provided'}
                  </div>
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Billing Address Line 2
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <div className="block w-full py-1.5 text-gray-900 sm:max-w-xl sm:text-sm sm:leading-6">
                    {formData.billing_address_2 || 'Not provided'}
                  </div>
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Billing City
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                    {formData.billing_city || 'Not provided'}
                  </div>
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Billing State / Province
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                    {formData.billing_state || 'Not provided'}
                  </div>
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Billing ZIP / Postal Code
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                    {formData.billing_zip_code || 'Not provided'}
                  </div>
                </div>
              </div>

              {/* Billing Contact Information */}
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Billing Contact Name
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <div className="block w-full py-1.5 text-gray-900 sm:max-w-xl sm:text-sm sm:leading-6">
                    {formData.billing_contact_name || 'Not provided'}
                  </div>
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Billing Contact Email
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <div className="block w-full py-1.5 text-gray-900 sm:max-w-md sm:text-sm sm:leading-6">
                    {formData.billing_contact_email || 'Not provided'}
                  </div>
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                  Billing Contact Phone
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                    {formData.billing_contact_phone || 'Not provided'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">Contact Information</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
            Primary contact person and email for this account.
          </p>

          <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0">
            
            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Contact Name
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xl sm:text-sm sm:leading-6">
                  {formData.contact_name || 'Not provided'}
                </div>
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Contact Email
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-md sm:text-sm sm:leading-6">
                  {formData.contact_email || 'Not provided'}
                </div>
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Contact Phone
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="block w-full py-1.5 text-gray-900 sm:max-w-xs sm:text-sm sm:leading-6">
                  {formData.contact_phone || 'Not provided'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button 
          type="button" 
          onClick={handleCancel}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Back to Customers
        </button>
      </div>
    </div>
  )
}